import axios from "axios";
import { requestHandler, successHandler, errorHandler } from "../interceptors";

const AGENT_BASE_URL = process.env.REACT_APP_AGENT_API_BASE_URL;

// In development the CRA proxy (setupProxy.js) forwards /agent, /sessions, /auth to
// AGENT_BASE_URL, so we use relative paths to avoid cross-origin preflight (OPTIONS).
// In production a reverse proxy (nginx) handles the same routing.
const AGENT_INSTANCE_BASE_URL = process.env.NODE_ENV === "development" ? "" : AGENT_BASE_URL;

export const agentAxiosInstance = axios.create({
  baseURL: AGENT_INSTANCE_BASE_URL,
  withCredentials: true,
  // Disable global blocking loader — agent chat uses its own inline loading states
  handlerEnabled: false,
});

agentAxiosInstance.interceptors.request.use((request) => requestHandler(request));

// Plain instance used only for the MCP refresh call to avoid interceptor recursion.
const _plainInstance = axios.create({ baseURL: AGENT_INSTANCE_BASE_URL, withCredentials: true });

agentAxiosInstance.interceptors.response.use(
  (response) => successHandler(response),
  async (error) => {
    const originalRequest = error.config;
    const mcpToken = localStorage.getItem("mcp_token");

    // Only attempt MCP refresh when the failing request carried the MCP Bearer token.
    const hasMcpBearer = mcpToken && originalRequest.headers?.Authorization === `Bearer ${mcpToken}`;

    if (error.response?.status === 401 && !originalRequest._mcpRetried && hasMcpBearer) {
      originalRequest._mcpRetried = true;

      const userId = getUserId();
      const mcpName = localStorage.getItem("mcp_name");

      try {
        const { data } = await _plainInstance.post("/auth/mcp_refresh", { userId, mcpName });
        const newToken = data.access_token;

        localStorage.setItem("mcp_token", newToken);
        window.dispatchEvent(new CustomEvent("mcp:token:changed", { detail: { token: newToken } }));

        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
        return agentAxiosInstance(originalRequest);
      } catch (refreshErr) {
        if (refreshErr.response?.status === 401) {
          localStorage.removeItem("mcp_token");
          localStorage.removeItem("mcp_name");
          window.dispatchEvent(new CustomEvent("mcp:token:changed", { detail: { token: null } }));

          const uid = getUserId();
          const redirectUrl = encodeURIComponent(`${window.location.origin}/oauth/callback`);
          window.location.href = `${AGENT_BASE_URL}/auth/session?userId=${uid}&clientName=CV+MCP&redirectUrl=${redirectUrl}`;
        }
        return errorHandler(refreshErr);
      }
    }

    return errorHandler(error);
  }
);

const getAdminData = () => {
  try {
    const adminData = localStorage.getItem("admin") || sessionStorage.getItem("admin"); 
    if (adminData) return JSON.parse(adminData);
  } catch (err) {
    console.error("Failed to get admin data from localStorage:", err);
  }
  return null;
};

const getUserId = () => getAdminData()?.id ?? null;

const sendMessage = async (payload) => {
  const token = localStorage.getItem("mcp_token");
  return await agentAxiosInstance.post("/agent/chat", payload, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
};

const getSessions = async () => {
  const userId = getUserId();
  if (!userId) throw new Error("User ID not found");
  return await agentAxiosInstance.get(`/sessions?userId=${userId}`);
};

const getSessionDetails = async (sessionId) =>
  await agentAxiosInstance.get(`/sessions/${sessionId}/messages`);

const stopAgent = async (sessionId) => {
  const adminData = getAdminData();
  const token = adminData?.auth_token;
  const userId = adminData?.id ?? null;
  return await agentAxiosInstance.post(
    "/agent/stop",
    { ...(sessionId ? { sessionId } : {}), userId },
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );
};

const startAuthSession = async ({ userId, clientName, redirectUrl }) =>
  await agentAxiosInstance.post("/auth/session", { userId, clientName, redirectUrl });

export { sendMessage, getSessions, getSessionDetails, stopAgent, getUserId, startAuthSession };
